/**
 * TestCaseImport Component
 * Allows users to import test cases from Excel file
 */

import React, { useState } from 'react';
import { apiClient, TestCase } from '../services/api';
import './TestCaseImport.css';

interface TestCaseImportProps {
  folderId: string;
  onImportComplete?: (testCases: TestCase[]) => void;
  onCancel?: () => void;
}

type ImportMode = 'excel' | 'xml';

interface XMLImportResult {
  success: boolean;
  summary: {
    folders_created: number;
    test_cases_created: number;
    test_cases_skipped: number;
    errors: Array<{
      type: string;
      name?: string;
      title?: string;
      reason: string;
    }>;
  };
}

export const TestCaseImport: React.FC<TestCaseImportProps> = ({
  folderId,
  onImportComplete,
  onCancel,
}) => {
  const [importMode, setImportMode] = useState<ImportMode>('excel');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importedTestCases, setImportedTestCases] = useState<TestCase[]>([]);
  const [xmlImportResult, setXmlImportResult] = useState<XMLImportResult | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file type based on import mode
      if (importMode === 'excel') {
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
          setError('Please select an Excel file (.xlsx or .xls)');
          return;
        }
      } else if (importMode === 'xml') {
        if (!file.name.endsWith('.xml')) {
          setError('Please select an XML file (.xml)');
          return;
        }
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiClient.downloadTestCaseTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'test_case_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('Please select a file to import');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (importMode === 'excel') {
        const testCases = await apiClient.importTestCasesFromExcel(folderId, selectedFile);
        setImportedTestCases(testCases);
        onImportComplete?.(testCases);
      } else if (importMode === 'xml') {
        const result = await apiClient.importTestCasesFromXML(folderId, selectedFile);
        setXmlImportResult(result);
        // Trigger refresh by calling onImportComplete with empty array
        onImportComplete?.([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import test cases');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setError(null);
    setImportedTestCases([]);
    setXmlImportResult(null);
  };

  return (
    <div className="test-case-import">
      <div className="import-header">
        <h2>Import Test Cases</h2>
        <p>Upload a file to import multiple test cases at once</p>
      </div>

      {/* Import Mode Selection */}
      <div className="import-mode-selector">
        <button
          className={`mode-btn ${importMode === 'excel' ? 'active' : ''}`}
          onClick={() => {
            setImportMode('excel');
            setSelectedFile(null);
            setError(null);
          }}
          disabled={isLoading}
        >
          📊 Excel Import
        </button>
        <button
          className={`mode-btn ${importMode === 'xml' ? 'active' : ''}`}
          onClick={() => {
            setImportMode('xml');
            setSelectedFile(null);
            setError(null);
          }}
          disabled={isLoading}
        >
          📄 TestRail XML Import
        </button>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {/* Excel Import Success */}
      {importedTestCases.length > 0 ? (
        <div className="import-success">
          <div className="success-message">
            ✅ Successfully imported {importedTestCases.length} test case(s)!
          </div>
          
          <div className="imported-test-cases-list">
            <h3>Imported Test Cases:</h3>
            {importedTestCases.map((testCase, index) => (
              <div key={testCase.id} className="imported-test-case-item">
                <span className="test-case-number">{index + 1}.</span>
                <div className="test-case-info">
                  <div className="test-case-description">
                    {testCase.test_case_description}
                  </div>
                  <div className="test-case-meta">
                    <span className={`priority-badge priority-${testCase.priority.toLowerCase()}`}>
                      {testCase.priority}
                    </span>
                    {testCase.tags && testCase.tags.length > 0 && (
                      <span className="tags-badge">{testCase.tags.join(', ')}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="import-actions">
            <button
              className="btn btn-primary"
              onClick={handleReset}
            >
              Import More
            </button>
            {onCancel && (
              <button
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Done
              </button>
            )}
          </div>
        </div>
      ) : xmlImportResult ? (
        /* XML Import Success */
        <div className="import-success">
          <div className="success-message">
            ✅ TestRail XML Import Completed!
          </div>
          
          <div className="xml-import-summary">
            <h3>Import Summary:</h3>
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">Folders Created:</span>
                <span className="stat-value">{xmlImportResult.summary.folders_created}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Test Cases Created:</span>
                <span className="stat-value success">{xmlImportResult.summary.test_cases_created}</span>
              </div>
              {xmlImportResult.summary.test_cases_skipped > 0 && (
                <div className="stat-item">
                  <span className="stat-label">Test Cases Skipped:</span>
                  <span className="stat-value warning">{xmlImportResult.summary.test_cases_skipped}</span>
                </div>
              )}
            </div>

            {xmlImportResult.summary.errors.length > 0 && (
              <div className="import-errors">
                <h4>⚠️ Errors/Warnings ({xmlImportResult.summary.errors.length}):</h4>
                <div className="error-list">
                  {xmlImportResult.summary.errors.slice(0, 10).map((err, index) => (
                    <div key={index} className="error-item">
                      <span className="error-type">[{err.type}]</span>
                      {err.name && <span className="error-name">{err.name}</span>}
                      {err.title && <span className="error-title">{err.title}</span>}
                      <span className="error-reason">: {err.reason}</span>
                    </div>
                  ))}
                  {xmlImportResult.summary.errors.length > 10 && (
                    <div className="error-item">
                      ... and {xmlImportResult.summary.errors.length - 10} more errors
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="import-actions">
            <button
              className="btn btn-primary"
              onClick={handleReset}
            >
              Import More
            </button>
            {onCancel && (
              <button
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Done
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="import-instructions">
            <h3>Instructions:</h3>
            
            {importMode === 'excel' ? (
              <>
                <ol>
                  <li>Download the Excel template</li>
                  <li>Fill in your test cases following the template format</li>
                  <li>Upload the completed Excel file</li>
                  <li>Click "Import" to add test cases to this folder</li>
                </ol>

                <div className="template-info">
                  <h4>Required Columns:</h4>
                  <ul>
                    <li><strong>Title</strong> or <strong>test_case_description</strong> - Description of the test case</li>
                    <li><strong>Expected Results</strong> or <strong>expected_result</strong> - Expected outcome</li>
                    <li><strong>Priority</strong> - Critical, High, Medium, or Low</li>
                  </ul>

                  <h4>Optional Columns:</h4>
                  <ul>
                    <li><strong>Steps</strong> or <strong>Test Steps</strong> - Test execution steps</li>
                    <li><strong>Preconditions</strong> - Prerequisites for the test</li>
                    <li><strong>Tags</strong> - Comma-separated tags</li>
                  </ul>

                  <div className="info-note">
                    <strong>💡 Flexible Format:</strong> The import supports common column name variations from TestRail and other tools. 
                    You can use either "Title" or "test_case_description", "Expected Results" or "expected_result", etc.
                  </div>
                </div>
              </>
            ) : (
              <>
                <ol>
                  <li>Export your test suite from TestRail as XML (Suite-level export)</li>
                  <li>Upload the XML file</li>
                  <li>Click "Import" to create folders and test cases</li>
                  <li>The import will preserve your folder hierarchy from TestRail sections</li>
                </ol>

                <div className="template-info">
                  <h4>What Gets Imported:</h4>
                  <ul>
                    <li><strong>Sections</strong> → Folders (with nested hierarchy)</li>
                    <li><strong>Test Cases</strong> → Test Cases (with all details)</li>
                    <li><strong>Priority</strong> → Normalized to system format</li>
                    <li><strong>Steps</strong> → Test execution steps</li>
                    <li><strong>Expected Results</strong> → Expected outcome</li>
                    <li><strong>Tags</strong> → Test case tags</li>
                  </ul>

                  <div className="info-note">
                    <strong>🚀 Batch Processing:</strong> The XML import supports large files with 800+ test cases and 38+ sections. 
                    Folders are created automatically with full hierarchy preservation.
                  </div>

                  <div className="info-note warning">
                    <strong>⚠️ Note:</strong> If a folder with the same name already exists, it will be reused instead of creating a duplicate.
                  </div>
                </div>
              </>
            )}
          </div>

          {importMode === 'excel' && (
            <div className="import-actions">
              <button
                className="btn btn-secondary"
                onClick={handleDownloadTemplate}
                disabled={isLoading}
              >
                📥 Download Template
              </button>
            </div>
          )}

          <div className="file-upload-section">
            <h3>Upload {importMode === 'excel' ? 'Excel' : 'XML'} File:</h3>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="import-file"
                accept={importMode === 'excel' ? '.xlsx,.xls' : '.xml'}
                onChange={handleFileSelect}
                disabled={isLoading}
              />
              <label htmlFor="import-file" className="file-input-label">
                {selectedFile ? selectedFile.name : `Choose ${importMode === 'excel' ? 'Excel' : 'XML'} file...`}
              </label>
            </div>

            {selectedFile && (
              <div className="file-info">
                <span>✓ File selected: {selectedFile.name}</span>
                <span>Size: {(selectedFile.size / 1024).toFixed(2)} KB</span>
              </div>
            )}
          </div>

          <div className="import-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={handleImport}
              disabled={isLoading || !selectedFile}
            >
              {isLoading ? 'Importing...' : 'Import Test Cases'}
            </button>
            {onCancel && (
              <button
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
